// Utility function to extract default structure and activity columns from dataset metadata

export function getDefaultColumns(
    dataset_metadata: any,
): { structureCol?: string, activityCol?: string } {
    if (!dataset_metadata) return {};

    const columnMetadata = dataset_metadata.column_metadata || {};
    let fieldOptions: { value: string, label: string }[] = [];
    if (columnMetadata) {
        fieldOptions = Object.keys(columnMetadata).map(meta => ({ value: meta, label: meta }));
    };
    const datasetTypes = dataset_metadata.dataset_types || [];
    const stages = dataset_metadata.stages || [];

    // Helper to check if a column is valid
    const isValid = (col: string) => fieldOptions.some(opt => opt.value === col);

    // --- Structure Column ---
    let structureCol: string | undefined;

    // 1. Try eclean_std_smiles stage
    const smilesStage = stages.find((s: any) => s.function === "eclean_std_smiles");
    if (smilesStage && smilesStage.function_params?.std_smiles_column) {
        const col = smilesStage.function_params.std_smiles_column;
        if (isValid(col)) structureCol = col;
    }

    // 2. Fallback: column_metadata with data_type "structure"
    if (!structureCol) {
        // Prefer curated columns (with class_origin referencing other columns)
        const structureCols = Object.entries(columnMetadata)
            .filter(([col, meta]: [string, any]) => meta?.data_type === "structure")
            .map(([col, meta]: [string, any]) => ({ col, meta }));

        // Prefer those with class_origin referencing another column
        const curated = structureCols.find(({ meta }) =>
            meta.class_origin?.class_origin_metadata?.associated_column
        );
        if (curated && isValid(curated.col)) structureCol = curated.col;

        // Otherwise, pick first valid structure column
        if (!structureCol) {
            const first = structureCols.find(({ col }) => isValid(col));
            if (first) structureCol = first.col;
        }
    }

    // --- Activity Column ---
    let activityCol: string | undefined;

    // Helper to find column by data_type
    const findColByDataType = (type: string) => {
        return Object.entries(columnMetadata)
            .find(([col, meta]: [string, any]) => meta?.data_type === type && isValid(col))?.[0];
    };

    // 1. Use dataset_types to determine approach
    if (datasetTypes.includes("single-class-label")) {
        // Try eclean_std_binary stage
        const binaryStage = stages.find((s: any) => s.function === "eclean_std_binary");
        if (binaryStage && binaryStage.function_params?.binary_value_column) {
            const col = binaryStage.function_params.binary_value_column;
            if (isValid(col)) activityCol = col;
        }
        // Fallback: column_metadata with data_type "single-class-label"
        if (!activityCol) activityCol = findColByDataType("single-class-label");
    } else if (datasetTypes.includes("continuous-value")) {
        // Try eclean_std_regression stage
        const regressionStage = stages.find((s: any) => s.function === "eclean_std_regression");
        if (regressionStage && regressionStage.function_params?.value_column) {
            const col = regressionStage.function_params.value_column;
            if (isValid(col)) activityCol = col;
        }
        // Fallback: column_metadata with data_type "continuous-value"
        if (!activityCol) activityCol = findColByDataType("continuous-value");
    } else if (datasetTypes.includes("structure-only") || datasetTypes.length === 0) {
        // Try stages for any relevant column
        const binaryStage = stages.find((s: any) => s.function === "eclean_std_binary");
        if (binaryStage && binaryStage.function_params?.binary_value_column) {
            const col = binaryStage.function_params.binary_value_column;
            if (isValid(col)) activityCol = col;
        }
        const regressionStage = stages.find((s: any) => s.function === "eclean_std_regression");
        if (!activityCol && regressionStage && regressionStage.function_params?.value_column) {
            const col = regressionStage.function_params.value_column;
            if (isValid(col)) activityCol = col;
        }
        // Fallback: column_metadata for any valid activity column
        if (!activityCol) {
            activityCol = findColByDataType("single-class-label") ||
                          findColByDataType("continuous-value");
        }
    }

    return { structureCol, activityCol };
}